import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import type { AcuityApproversResponse } from "@/core/types";
import { admin_cse } from "@/config/roles";
import { MEDIUM_LIVED_CACHE } from "@/lib/constants/cache";
import { selectRole } from "@/redux/selectors/authSelectors";

import { acuityKeys } from "../keyFactory";
import { getAcuityApprovers } from "../queryFunctions/getAcuityApprovers";

type UseAcuityApproversProps<TData = AcuityApproversResponse> = {
  enabled?: boolean;
  select?: (data: AcuityApproversResponse) => TData;
};

type AcuityApproversQueryOptions = UseQueryOptions<
  AcuityApproversResponse,
  unknown,
  AcuityApproversResponse,
  ReturnType<typeof acuityKeys.approvers>
>;

export function useAcuityApprovers<TData = AcuityApproversResponse>({
  enabled = true,
  select,
}: UseAcuityApproversProps<TData> = {}) {
  const role = useSelector(selectRole);

  return useQuery({ ...optionFactory(role === admin_cse && enabled), select });
}

function optionFactory(enabled: boolean) {
  return {
    enabled,
    queryFn: getAcuityApprovers,
    queryKey: acuityKeys.approvers(),
    staleTime: MEDIUM_LIVED_CACHE,
  } satisfies AcuityApproversQueryOptions;
}
